import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { withTranslation } from 'react-i18next';
import { Grid, Tooltip } from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { Card, Typography } from '@totalsoft/rocket-ui';
import ThinDivider from 'components/common/ThinDivider';
import { ThemedTypography } from 'components/common/CommonStyles';

export const OfferSummaryComponent = ({ offerSummary, isIconCard, t }) =>
  isIconCard ? (
    <Card icon={AssignmentIcon} title={t('Offer.Summary.Title')}>
      {offerSummary.productIsSelected && (
        <Grid container spacing={1}>
          <Grid item container mb={2}>
            {offerSummary.assets &&
              offerSummary.assets.length > 0 &&
              offerSummary.assets.map(asset => {
                return (
                  <Grid item xs={12} key={asset.documentDetailId}>
                    <ThemedTypography textTransform='uppercase' color='success'>
                      {asset.quantity} X {asset.assetName}
                    </ThemedTypography>
                  </Grid>
                );
              })}
          </Grid>
          <Grid item xs={12} md={6}>
            {t('Offer.Summary.TotalAcquisitionPrice')}
          </Grid>
          <Grid item xs={12} md={6} textAlign='end'>
            <span>
              {offerSummary.documentCurrencyCode} {t('DECIMAL', { number: offerSummary.totalValue })}
            </span>
          </Grid>
          {!offerSummary.hideInfoForDealersByContractTypes && (
            <>
              <Grid item xs={12} md={6}>
                {t('Offer.Summary.FinancedAmount')}
              </Grid>
              <Grid item xs={12} md={6} textAlign='end'>
                <span>
                  {offerSummary.documentCurrencyCode} {t('DECIMAL', { number: offerSummary.financedAmount })}
                </span>
              </Grid>
            </>
          )}
          <Grid item xs={12} md={6}>
            {t('Offer.Summary.Period')}
          </Grid>
          <Grid item xs={12} md={6} textAlign='end'>
            <span>{t('NUMBER', { number: offerSummary.period })}</span>
          </Grid>

          <Grid item xs={12} md={6}>
            {t('Offer.Summary.DownPayment')}
          </Grid>
          <Grid item xs={12} md={6} textAlign='end'>
            <span>{t('NUMBER', { number: offerSummary.downPayment })}%</span>
          </Grid>
          {!offerSummary.hideInfoForDealersByContractTypes && (
            <>
              <Grid item xs={12} md={6}>
                {t('Offer.Summary.ResidualValue')}
              </Grid>
              <Grid item xs={12} md={6} textAlign='end'>
                {offerSummary.residualValue != null && <span>{t('NUMBER', { number: offerSummary.residualValue })}%</span>}
                {offerSummary.residualValue == null && <span>-</span>}
              </Grid>
            </>
          )}
          {offerSummary.showCreditOfferAddInfo ? (
            <Tooltip title={t('Offer.Summary.Message')}>
              <Grid item xs={12} md={6}>
                {t('Offer.Summary.CreditContractValue')}
              </Grid>
            </Tooltip>
          ) : (
            <Grid item xs={12} md={6}>
              {t('Offer.Summary.ContractValue')}
            </Grid>
          )}
          <Grid item xs={12} md={6} textAlign='end'>
            <span>
              {offerSummary.documentCurrencyCode} {t('NUMBER', { number: offerSummary.contractValue })}
            </span>
          </Grid>
          {!offerSummary.hideInfoForDealersByContractTypes && (
            <>
              <Grid item xs={12} md={6}>
                {t('Offer.Summary.InterestRate')}
              </Grid>
              <Grid item xs={12} md={6} textAlign='end'>
                <span>{t('PERCENT_NUMBER', { number: offerSummary.interestRate })}%</span>
              </Grid>
            </>
          )}

          {!offerSummary.hideEIR && (
            <>
              <Grid item xs={12} md={6}>
                <Typography fontWeight='bold' color='error'>
                  {t('Offer.Summary.EIR')}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6} textAlign='end'>
                <Typography fontWeight='bold' color='error'>
                  {t('PERCENT_NUMBER', { number: offerSummary.eir })}%
                </Typography>
              </Grid>
            </>
          )}

          <Grid item xs={12} md={12}>
            <ThinDivider />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant='button' fontWeight='bold' color='error'>
              {t('Offer.Summary.MonthlyRateValueWithoutVAT')}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6} textAlign='end'>
            <Typography variant='button' fontWeight='bold' color='error'>
              {offerSummary.documentCurrencyCode} {t('NUMBER', { number: offerSummary.monthlyRateValueWithoutVat })}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <ThemedTypography variant='button' color='success'>
              {t('Offer.Summary.MonthlyRateValue')}
            </ThemedTypography>
          </Grid>
          <Grid item xs={12} md={6} textAlign='end'>
            <ThemedTypography variant='button' color='success'>
              {offerSummary.documentCurrencyCode} {t('NUMBER', { number: offerSummary.monthlyRateValue })}
            </ThemedTypography>
          </Grid>
        </Grid>
      )}
    </Card>
  ) : (
    <Grid container spacing={1} alignItems='center' px={3} py={2}>
      <Grid item xs={12}>
        <Typography textTransform='uppercase' color='primary' fontWeight='bold' variant='subtitle1'>
          {t('Offer.Summary.Title')}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <ThinDivider />
      </Grid>
      {offerSummary.productIsSelected && (
        <>
          <Grid item container xs={12} mb={2}>
            {offerSummary.assets &&
              offerSummary.assets.length > 0 &&
              offerSummary.assets.map(asset => {
                return (
                  <Grid item xs={12} key={asset.documentDetailId}>
                    <ThemedTypography textTransform='uppercase' color='success'>
                      {asset.quantity} X {asset.assetName}
                    </ThemedTypography>
                  </Grid>
                );
              })}
          </Grid>
          <Grid item xs={12} md={6}>
            {t('Offer.Summary.TotalAcquisitionPrice')}
          </Grid>
          <Grid item xs={12} md={6} textAlign='end'>
            {offerSummary.totalValue ? offerSummary.documentCurrencyCode : null} {t('DECIMAL', { number: offerSummary.totalValue })}
          </Grid>
          {!offerSummary.hideInfoForDealersByContractTypes && (
            <>
              <Grid item xs={12} md={6}>
                {t('Offer.Summary.FinancedAmount')}
              </Grid>
              <Grid item xs={12} md={6} textAlign='end'>
                <span>
                  {offerSummary.documentCurrencyCode} {t('DECIMAL', { number: offerSummary.financedAmount })}
                </span>
              </Grid>
            </>
          )}
          <Grid item xs={12} md={6}>
            {t('Offer.Summary.Period')}
          </Grid>
          <Grid item xs={12} md={6} textAlign='end'>
            <span>{t('NUMBER', { number: offerSummary.period })}</span>
          </Grid>

          <Grid item xs={12} md={6}>
            {t('Offer.Summary.DownPayment')}
          </Grid>
          <Grid item xs={12} md={6} textAlign='end'>
            <span>{t('PERCENT_NUMBER', { number: offerSummary.downPayment })}%</span>
          </Grid>

          {!offerSummary.hideInfoForDealersByContractTypes && (
            <>
              <Grid item xs={12} md={6}>
                {t('Offer.Summary.ResidualValue')}
              </Grid>
              <Grid item xs={12} md={6} textAlign='end'>
                {offerSummary.residualValue != null && <span>{t('PERCENT_NUMBER', { number: offerSummary.residualValue })}%</span>}
                {offerSummary.residualValue == null && <span>-</span>}
              </Grid>
            </>
          )}
          {offerSummary.showCreditOfferAddInfo ? (
            <Tooltip title={t('Offer.Summary.Message')}>
              <Grid item xs={12} md={6}>
                {t('Offer.Summary.CreditContractValue')}
              </Grid>
            </Tooltip>
          ) : (
            <Grid item xs={12} md={6}>
              {t('Offer.Summary.ContractValue')}
            </Grid>
          )}
          <Grid item xs={12} md={6} textAlign='end'>
            <span>
              {offerSummary.documentCurrencyCode} {t('NUMBER', { number: offerSummary.contractValue })}
            </span>
          </Grid>
          {!offerSummary.hideInfoForDealersByContractTypes && (
            <>
              <Grid item xs={12} md={6}>
                {t('Offer.Summary.InterestRate')}
              </Grid>
              <Grid item xs={12} md={6} textAlign='end'>
                <span>{t('PERCENT_NUMBER', { number: offerSummary.interestRate })}%</span>
              </Grid>
            </>
          )}
          {!offerSummary.hideEIR && (
            <>
              <Grid item xs={12} md={6}>
                <Typography fontWeight='bold' color='error'>
                  {t('Offer.Summary.EIR')}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6} textAlign='end'>
                <Typography fontWeight='bold' color='error'>
                  {t('PERCENT_NUMBER', { number: offerSummary.eir })}%
                </Typography>
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <ThinDivider />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant='button' fontWeight='bold' color='error'>
              {t('Offer.Summary.MonthlyRateValueWithoutVAT')}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6} textAlign='end'>
            <Typography variant='button' fontWeight='bold' color='error'>
              {offerSummary.documentCurrencyCode} {t('DECIMAL', { number: offerSummary.monthlyRateValueWithoutVat })}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <ThemedTypography variant='button' color='success'>
              {t('Offer.Summary.MonthlyRateValue')}
            </ThemedTypography>
          </Grid>
          <Grid item xs={12} md={6} textAlign='end'>
            <ThemedTypography variant='button' color='success'>
              {offerSummary.documentCurrencyCode} {t('DECIMAL', { number: offerSummary.monthlyRateValue })}
            </ThemedTypography>
          </Grid>
        </>
      )}
    </Grid>
  );

OfferSummaryComponent.propTypes = {
  offerSummary: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  isIconCard: PropTypes.bool.isRequired
};

export default compose(withTranslation())(memo(OfferSummaryComponent));
